import { store } from "@store";
import { logoutSuccess, selectToken } from "@store/slices/authSlice";
import { errorToast } from "@utils/constants";
import { AxiosError, InternalAxiosRequestConfig } from "axios";

// Flag to track if an auth error toast has been shown recently
let authErrorToastShown = false;
let authErrorToastTimer: NodeJS.Timeout | null = null;

// Reset the auth error toast flag after a delay
const resetAuthErrorToastFlag = () => {
  if (authErrorToastTimer) {
    clearTimeout(authErrorToastTimer);
  }

  authErrorToastTimer = setTimeout(() => {
    authErrorToastShown = false;
    authErrorToastTimer = null;
  }, 3000); // Reset after 3 seconds
};

function forceLogout(message: string) {
  if (!authErrorToastShown) {
    authErrorToastShown = true;

    alert(message || "Session expired, please login again");
    resetAuthErrorToastFlag();

    store.dispatch(logoutSuccess());
    window.location.href = "/#/auth/signin";
  }
}

/** List/read endpoints where 404 is handled by the screen (empty state), not global toasts */
function isHandledByScreenUrl(url: string | undefined): boolean {
  if (!url) return false;

  return (
    url.includes("/sch/api/v1/list") ||
    url.includes("/sch/api/v1/group-admin/list") ||
    url.includes("/stdnt/api/v1/list") ||
    url.includes("/stdnt/api/v1/class-dashboard") ||
    url.includes("/stdnt/api/v2/list") ||
    url.includes("/class/api/v1/assign") ||
    url.includes("/tch/api/v1/list") ||
    url.includes("/cs/api/v1/list") ||
    url.includes("/cs/api/v1/view/") ||
    url.includes("/class/api/v1/list/our-classes") ||
    url.includes("/class/api/v1/by-level/") ||
    url.includes("/class/api/v1/view/") ||
    url.includes("/class/api/v1/checkout") ||
    url.includes("/class/api/v1/student/by-email") ||
    url.includes("/class/api/v1/list") ||
    url.includes("/class/api/v1/level/") ||
    url.includes("/asgmt/api/v1/list") ||
    url.includes("/asgmt/api/v1/subsn/list") ||
    url.includes("/asgmt/api/v1/subsnqry/list") ||
    url.includes("/asgmt/api/v1/fdbk/list") ||
    url.includes("/asgmt/api/v1/qry/detail/list") ||
    url.includes("/asgmt/api/v1/detail/list") ||
    url.includes("/auth/api/v1/stdnt/logs/list") ||
    url.includes("/auth/api/v1/sch/logs/list") ||
    url.includes("/auth/api/v1/teacher/logs/list") ||
    url.includes("/notify/api/v1/mark-viewed")
  );
}

function get403LogoutMessage(errorMessage: string): string {
  if (/inactive|not active|deactivated|disabled/i.test(errorMessage)) {
    return "Your account is inactive. Please contact your administrator.";
  }

  return errorMessage || "You do not have permission to access this resource. Please login again.";
}

export const requestSuccess = (config: InternalAxiosRequestConfig) => {
  const token = selectToken(store.getState());

  if (token) {
    // Add authorization token to headers if user is authenticated
    config.headers.Authorization = `Token ${token}`;
  }

  return config;
};

export const requestFailure = async (error: AxiosError) => {
  // Extract error details
  const status = error.response?.status;
  const isSessionExpired = status === 401;

  // Safely extract error message with proper type checking
  let errorMessage = "An error occurred";
  if (error.response?.data && typeof error.response.data === "object") {
    const data = error.response.data as Record<string, any>;

    if (data.detail && typeof data.detail === "string") {
      errorMessage = data.detail;
    }

    if (data.message && typeof data.message === "string") {
      errorMessage = data.message;
    }
  } else if (error.message) {
    errorMessage = error.message;
  }

  // 401/403 → alert, logout, redirect to sign-in
  if (isSessionExpired) {
    forceLogout("Session expired, please login again");
  } else if (status === 403) {
    forceLogout(get403LogoutMessage(errorMessage));
  } else {
    // List/read 404 — screen shows empty state
    if (status === 404 && isHandledByScreenUrl(error.config?.url)) {
      return Promise.reject(error);
    }

    // For non-auth errors, always show the toast
    if (!error.config?.skipGlobalErrorToast) {
      errorToast("Error", errorMessage);
    }
  }

  return Promise.reject(error);
};
